import React, { useState, useEffect } from 'react';
import {
    Row, Col, Form, Label, Input, Card, CardBody,
    Container, Button, FormFeedback, InputGroup
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPromotionsEditData } from "../../store/promotions/actions";
import "./CustomCheckbox.css";
import "flatpickr/dist/themes/material_blue.css";
import Flatpickr from "react-flatpickr";

const DiscountsEdit = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { data } = useSelector(state => state.Promotions.edit);

    const [promotionId, setId] = useState(id);
    const [title, setTitle] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [description, setDescription] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isLoading, setLoading] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            dispatch(fetchPromotionsEditData(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (data?.data) {
            setId(data.data.id || id);
            setTitle(data.data.title || "");
            setDescription(data.data.description || "");
            setImagePreview(data.data.image || null);

            // Convert API date string → Date object
            setStartDate(data.data.start_date ? new Date(data.data.start_date) : null);
            setEndDate(data.data.end_date ? new Date(data.data.end_date) : null);
        }
    }, [data, id]);

    const [validation, setValidation] = useState({
        title: null,
        image: null,
        description: null,
        startDate: null,
        endDate: null,
    });

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        } else {
            setImageFile(null);
            setImagePreview(null);
        }
    };

    const validateFields = () => {
        let isValid = true;
        const newValidation = {};

        newValidation.title = title.trim() !== '';
        newValidation.startDate = startDate !== null;
        newValidation.endDate = endDate !== null;
        newValidation.description = description.trim() !== '';
        newValidation.image = imageFile !== null || imagePreview !== null;

        setValidation(newValidation);

        Object.values(newValidation).forEach(v => {
            if (v !== true) isValid = false;
        });

        return isValid;
    };

    const formatDate = (date) => {
        if (!date) return "";
        return date.toISOString().split("T")[0];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateFields()) return;

        const formData = new FormData();
        formData.append('id', promotionId);
        formData.append('title', title);
        formData.append('start_date', formatDate(startDate));
        formData.append('end_date', formatDate(endDate));
        formData.append('description', description);
        if (imageFile) formData.append('image', imageFile);

        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/promotions/update`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: formData,
            });

            const result = await response.json();

            if (response.status === 200) {
                toast.success('Promotion updated successfully', { autoClose: 3000 });
                navigate(`/promotions`);
            } else if (response.status === 422) {
                if (result?.errors) {
                    Object.values(result.errors).forEach((errorArr) => {
                        errorArr.forEach((errMsg) => {
                            toast.error(errMsg, { autoClose: 3000 });
                        });
                    });
                } else {
                    toast.error(result?.message || 'Validation failed', { autoClose: 3000 });
                }
            } else {
                toast.error(result?.message || `Error: ${response.statusText}`, { autoClose: 3000 });
            }
        } catch (err) {
            console.log(err);
            toast.error("Failed to update promotion", { autoClose: 3000 });
        } finally {
            setLoading(false);
        }
    };

    document.title = "Edit Promotion | Moeen";

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs maintitle="Home" title="Promotions" breadcrumbItem="Edit Promotion" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md="6">
                                            {/* Title */}
                                            <div className="mb-3">
                                                <Label>Title</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter title"
                                                    value={title}
                                                    onChange={e => setTitle(e.target.value)}
                                                    valid={validation["title"] === true}
                                                    invalid={validation["title"] !== true && validation["title"] !== null}
                                                />
                                                <FormFeedback tooltip>Title is required.</FormFeedback>
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Image */}
                                            <div className="mb-3">
                                                <Label>Image</Label>
                                                <Input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                />

                                                {imagePreview && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <img
                                                            src={imagePreview}
                                                            alt="Preview"
                                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 4, border: '1px solid #ddd' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <div className="mb-3">
                                                <Label>Start Date</Label>
                                                <InputGroup>
                                                    <Flatpickr
                                                        className="form-control d-block"
                                                        placeholder="dd M, yyyy"
                                                        value={startDate}
                                                        onChange={(date) => setStartDate(date[0] || null)}
                                                        options={{
                                                            altInput: true,
                                                            altFormat: "F j, Y",
                                                            dateFormat: "Y-m-d",
                                                            minDate: startDate ? startDate : "today",
                                                        }}
                                                    />
                                                </InputGroup>
                                                {validation.startDate === false && (
                                                    <FormFeedback tooltip>Start Date is required.</FormFeedback>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md="6">
                                            <div className="mb-3">
                                                <Label>End Date</Label>
                                                <InputGroup>
                                                    <Flatpickr
                                                        className="form-control d-block"
                                                        placeholder="dd M, yyyy"
                                                        value={endDate}
                                                        onChange={(date) => setEndDate(date[0] || null)}
                                                        options={{
                                                            altInput: true,
                                                            altFormat: "F j, Y",
                                                            dateFormat: "Y-m-d",
                                                            minDate: startDate ? startDate : "today",
                                                        }}
                                                    />
                                                </InputGroup>
                                                {validation.endDate === false && (
                                                    <FormFeedback tooltip>End Date is required.</FormFeedback>
                                                )}
                                            </div>
                                        </Col>

                                        {/* Description */}
                                        <Col md="12">
                                            <div className="mb-3">
                                                <Label>Description</Label>
                                                <Input
                                                    type="textarea"
                                                    rows="5"
                                                    placeholder="Enter description"
                                                    value={description}
                                                    onChange={e => setDescription(e.target.value)}
                                                    valid={validation["description"] === true}
                                                    invalid={validation["description"] !== true && validation["description"] !== null}
                                                />
                                                <FormFeedback tooltip>Description is required.</FormFeedback>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col className="text-end">
                                            <Button type="submit" color="primary">
                                                {isLoading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Updating
                                                    </>
                                                ) : (
                                                    "Update"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default DiscountsEdit;
